import { isNil } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { StyleRulesCallback, withStyles } from '@material-ui/core/styles';
import {
    Grid,
    Typography,
    Button,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';

import { DEFAULT_ROLE_ORG_ADMIN } from 'yeself-common/defaults/ApplicationDefaults';
import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';
import { Company } from 'yeself-common/typings/application/Application';

import { makeSelectIsRoleIncluded } from 'yeself-client-common/security/SecuritySelector';

import Feature from 'yeself-client-common/featureFlags/components/Feature';

import {
    updateDetailSettings,
    duplicateApplication,
} from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectAppId,
    selectApplicationName,
    selectIsApplicationActive,
    selectApplicationSystem,
    selectCompanySystem,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormInput from 'Console/components/common/form/FormInput';
import FormItemSwitch from 'Console/components/common/form/FormItemSwitch';
import OriginUrls from 'Console/settings/originUrls/components/OriginUrls';
import TrustedDomains from 'Console/settings/trustedDomains/components/TrustedDomains';

const styles: StyleRulesCallback = (theme) => ({
    root: {
        flexGrow: 1,
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
        width: '80%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
        },
    },
    title: {
        display: 'inline-block',
        fontWeight: 'bold',
        paddingBottom: theme.typography.pxToRem(20),
        paddingTop: theme.typography.pxToRem(30),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        paddingBottom: theme.typography.pxToRem(25),
    },
    spaceBottom: {
        paddingBottom: theme.typography.pxToRem(50),
    },
    btnAdd: {
        marginTop: theme.typography.pxToRem(15),
        marginLeft: theme.typography.pxToRem(47),
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
        },
    },
    btn: {
        marginBottom: theme.typography.pxToRem(40),
    },

    subTitleActive: {
        paddingLeft: theme.typography.pxToRem(16),
        paddingRight: theme.typography.pxToRem(18),
        [theme.breakpoints.down('sm')]: {
            paddingLeft: 0,
        },
    },
    listItems: {
        paddingTop: theme.typography.pxToRem(20),
        paddingBottom: theme.typography.pxToRem(20),
    },
    listItem: {
        padding: 0,
    },
    boxList: {
        width: '57%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    formInput: {
        width: '42%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: '5%',
        },
    },
    formName: {
        width: '10%',
        marginRight: '5%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginRight: 0,
        },
    },
    listItemText: {
        overflow: 'auto',
        textOverflow: 'ellipsis',
    },
    expandedContainer: {
        padding: '0px',
        backgroundColor: '#FAFAFA',
        minHeight: '21px',
        maxHeight: '41px',
    },
    expandedContent: {
        padding: '0px 0px 25px 0px',
        backgroundColor: '#FAFAFA',
    },
});

const General = ({ classes, intl, form, form: { getFieldValue }, match }) => {
    const dispatch = useDispatch();

    const selectUserRole = useMemo(makeSelectIsRoleIncluded, []);

    const appID = useSelector(selectAppId);
    const name = useSelector(selectApplicationName);
    const isActive = useSelector(selectIsApplicationActive);
    const system = useSelector(selectApplicationSystem);
    const company = useSelector(selectCompanySystem);
    const isRoleOrganizationAdmin = useSelector((state) =>
        selectUserRole(state, {
            includedRole: DEFAULT_ROLE_ORG_ADMIN,
        })
    );

    const handleDuplicateApplication = useCallback(() => {
        dispatch(
            duplicateApplication(appID, {
                success: (
                    <FormattedMessage
                        id="Notification.duplicate.application.success"
                        defaultMessage="Your space was successfully duplicated. Please go through the spaces section"
                    />
                ),
                fail: (
                    <FormattedMessage
                        id="Notification.duplicate.application.fail"
                        defaultMessage="Your space was not duplicated, something is wrong"
                    />
                ),
            })
        );
    }, [dispatch, appID]);

    const handleSaveSettings = useCallback(() => {
        const { name, active, systemSlug, companyId } = getFieldValue('general');
        const payload: {
            name: string;
            active: boolean;
            system?: { slug: string };
            company?: Company;
        } = {
            name,
            active,
        };
        if (!isNil(systemSlug)) payload.system = { slug: systemSlug };
        if (!isNil(companyId)) payload.company = { id: Number(companyId) };
        if (companyId === '') payload.company = { id: null };

        dispatch(
            updateDetailSettings(payload, {
                success: (
                    <FormattedMessage id="Notification.save" defaultMessage="Your settings were saved" />
                ),

                fail: (
                    <FormattedMessage
                        id="Notification.fail"
                        defaultMessage="Unable to save, something went wrong"
                    />
                ),
            })
        );
    }, [dispatch, getFieldValue]);

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.title}>
                    <FormattedMessage
                        id="Settings.general.applicationDetail"
                        defaultMessage="Application detail"
                    />
                </Typography>
            </Grid>
            <Grid container direction="row" alignItems="flex-end" style={{ paddingBottom: '20px' }}>
                <Typography variant="body1" className={classes.formName}>
                    <FormattedMessage id="Settings.general.name" defaultMessage="Name" />
                </Typography>

                <Grid className={classes.formInput}>
                    <FormInput
                        fullWidth={false}
                        form={form}
                        type="text"
                        formItemKey="general.name"
                        placeholder={intl.formatMessage({ id: 'Settings.general.label' })}
                        initialValue={name}
                    />
                </Grid>
            </Grid>

            <Feature name={FEATURE_FLAG_NAMES.APPLICATION_STATUS_TOGGLE}>
                <Grid container direction="row" alignItems="center">
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage id="Buttons.switchActive" defaultMessage="Active" />
                    </Typography>

                    <FormItemSwitch initialValue={isActive} form={form} formItemKey="general.active" />
                </Grid>
            </Feature>
            <Feature name={FEATURE_FLAG_NAMES.ADOPT_1_0_SYSTEM}>
                <Grid container direction="row" alignItems="flex-end" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage id="Settings.general.system" defaultMessage="System" />
                    </Typography>

                    <Grid className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            form={form}
                            type="text"
                            formItemKey="general.systemSlug"
                            placeholder={intl.formatMessage({ id: 'Settings.general.label' })}
                            initialValue={system.slug}
                        />
                    </Grid>
                </Grid>
            </Feature>
            <Feature name={FEATURE_FLAG_NAMES.ADOPT_1_0_COMPANY}>
                <Grid container direction="row" alignItems="flex-end" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage id="Settings.general.companyid" defaultMessage="Company ID" />
                    </Typography>

                    <Grid className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            form={form}
                            type="number"
                            formItemKey="general.companyId"
                            placeholder={intl.formatMessage({ id: 'Settings.general.id' })}
                            initialValue={company.id}
                        />
                    </Grid>
                </Grid>
            </Feature>
            <Grid container>
                <Grid item className={classes.boxList}>
                    <Grid container alignItems="center">
                        <Typography variant="button">
                            <Button
                                id="yeself-button"
                                variant="contained"
                                color="primary"
                                onClick={handleSaveSettings}
                                className={classes.btn}
                            >
                                <FormattedMessage id="Buttons.save" defaultMessage="Save" />
                            </Button>
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={8}>
                    <TrustedDomains applicationId={match.params.appId} />
                </Grid>
                <Feature name={FEATURE_FLAG_NAMES.EXTENSION_URLS}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <ExpansionPanel className={classes.expandedContent} defaultExpanded>
                            <ExpansionPanelSummary
                                id="yeself-expanded-origin-urls"
                                className={classes.expandedContainer}
                                expandIcon={<ExpandMore />}
                            >
                                <Typography variant="subtitle1">
                                    <FormattedMessage
                                        id="Settings.general.domains"
                                        defaultMessage="Extension URLs"
                                    />
                                </Typography>
                            </ExpansionPanelSummary>
                            <ExpansionPanelDetails className={classes.expandedContent}>
                                <OriginUrls applicationId={match.params.appId} />
                            </ExpansionPanelDetails>
                        </ExpansionPanel>
                    </Grid>
                </Feature>
            </Grid>

            {isRoleOrganizationAdmin && (
                <Grid container direction="column">
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage
                            id="Settings.general.duplicateApplication"
                            defaultMessage="Clone space"
                        />
                    </Typography>

                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage
                            id="Settings.general.duplicateApplication.message"
                            defaultMessage="Pressing the Perform button duplicates the space with all settings and guides."
                        />
                    </Typography>

                    <Typography variant="button">
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            className={classes.btn}
                            onClick={handleDuplicateApplication}
                        >
                            <FormattedMessage id="Buttons.perform'" defaultMessage="Perform" />
                        </Button>
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(General));
